import React, { useMemo, useEffect } from "react";

import { ApolloProvider } from "@apollo/client";
import * as Sentry from "@sentry/react";
import { Integrations } from "@sentry/tracing";
import "core-js/stable";
import { IntercomProvider } from "react-use-intercom";

import flatten from "flat";
import { merge } from "lodash";
import { IntlProvider } from "react-intl";
import "regenerator-runtime/runtime";
import { ChakraProvider, CSSReset } from "@chakra-ui/react";
import AvatarEditorProvider from "./components/utils/AvatarEditorProvider";

import apolloClient from "./apolloClient";
import ViewerProvider from "./components/ViewerProvider";
import { WombatThemeProvider } from "./components/wombat-ui";
import useViewer from "./hooks/useViewer";
import DefaultEnglish from "./locales/en-US.json";
import DefaultFrench from "./locales/fr-FR.json";
import AppRouter from "./routers/AppRouter";
import ConfirmProvider from "./components/utils/confirm/ConfirmContext";
import { BreadcrumbProvider } from "./hooks/useBreadcrumbs";
import theme from "./theme/theme";
import "./theme/theme.css";

const sentryDSN = document
  .querySelector('meta[name="sentry-dsn"]')
  ?.getAttribute("content");
/* istanbul ignore if  */
if (sentryDSN) {
  Sentry.init({
    dsn: sentryDSN,
    integrations: [new Integrations.BrowserTracing()],
    tracesSampleRate: 1.0,
  });
}

const LocalizedApp = () => {
  const user = useViewer();

  const locale = user ? user.locale : navigator.language || "fr-FR";
  const language = locale.split("-")[0];

  // Set the <html lang="XX" /> attribute
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const customKeys = useMemo(
    () =>
      user?.company.translations.reduce(
        (customKeys, { key, value, locale }) => {
          if (!customKeys[locale]) {
            customKeys[locale] = {};
          }

          customKeys[locale][key] = value;

          return customKeys;
        },
        { en: {}, fr: {} }
      ),
    [user?.company.translations]
  );

  const translationsByLanguages = useMemo(() => {
    return [
      {
        language: "en",
        translations: merge(DefaultEnglish, customKeys?.en),
      },
      {
        language: "fr",
        translations: merge(DefaultFrench, customKeys?.fr),
      },
    ];
  }, [customKeys]);

  const translations = useMemo(
    () =>
      flatten(
        translationsByLanguages.find((t) => t.language === language)
          ?.translations || DefaultFrench,
        { safe: true }
      ),
    [translationsByLanguages, language]
  );

  return (
    <IntlProvider locale={locale} messages={translations}>
      <AvatarEditorProvider>
        <AppRouter />
      </AvatarEditorProvider>
    </IntlProvider>
  );
};

const App = () => {
  return (
    <ApolloProvider client={apolloClient}>
      <ViewerProvider>
        <IntercomProvider
          appId={document.querySelector('meta[name="intercom-app-id"]')?.getAttribute('content')}
        >
          <ChakraProvider
            theme={theme}
          >
            <CSSReset />
            <ConfirmProvider>
              <BreadcrumbProvider>
                <WombatThemeProvider>
                  <LocalizedApp />
                </WombatThemeProvider>
              </BreadcrumbProvider>
            </ConfirmProvider>
          </ChakraProvider>
        </IntercomProvider>
      </ViewerProvider>
    </ApolloProvider>
  );
};

export default App;
